import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { hasCardAccess, hasCardAction } from '../utils/permissionHelper';
import axiosInstance from '../api/axiosConfig';
import receptionBg from '../assets/images/backgrounds/reception-bg.png';
import './DocumentLogBook.css';

const DocumentLogBook = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const canvasRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const drawingHandlersRef = useRef({ startDrawing: null, draw: null, stopDrawing: null });

    // Check if user has document-log-book permission
    // Only system_admin and system_receptionist should have full access (Create and All Pending tabs)
    // All users can see "My Pending Verification" and "Completed" tabs
    const userPermissions = user?.permissions || { cards: [], actions: [] };
    const hasDocumentLogBookAccess = user?.role === 'system_admin' || hasCardAccess(userPermissions, 'document-log-book');
    const hasFullAccess = user?.role === 'system_admin' || user?.role === 'system_receptionist';

    const [activeTab, setActiveTab] = useState(hasFullAccess ? 'create' : 'myPending');
    
    // Users for dropdown
    const [users, setUsers] = useState({});
    const [loadingUsers, setLoadingUsers] = useState(false);
    
    // Create Entry Form
    const [formData, setFormData] = useState({
        from: '',
        fromType: 'user',
        fromUserId: '',
        to: '',
        toType: 'user',
        toUserId: '',
        receiver: '',
        receiverType: 'user',
        receiverUserId: '',
        documents: '',
        additionalVerifier1: '',
        additionalVerifier2: ''
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Expanded documents state (tracks which entry IDs have expanded docs text)
    const [expandedDocs, setExpandedDocs] = useState({});
    const toggleDocExpand = (id) => setExpandedDocs(prev => ({ ...prev, [id]: !prev[id] }));
    const DOC_TRUNCATE_LEN = 60;

    // My Pending Entries (for all users)
    const [myPendingEntries, setMyPendingEntries] = useState([]);
    const [loadingMyPending, setLoadingMyPending] = useState(false);
    const [searchMyPending, setSearchMyPending] = useState('');
    const [currentMyPendingPage, setCurrentMyPendingPage] = useState(1);
    const [totalMyPendingPages, setTotalMyPendingPages] = useState(1);

    // All Pending Entries (Admin/Receptionist only)
    const [allPendingEntries, setAllPendingEntries] = useState([]);
    const [loadingAllPending, setLoadingAllPending] = useState(false);
    const [searchAllPending, setSearchAllPending] = useState('');
    const [currentAllPendingPage, setCurrentAllPendingPage] = useState(1);
    const [totalAllPendingPages, setTotalAllPendingPages] = useState(1);

    // Completed Entries
    const [completedEntries, setCompletedEntries] = useState([]);
    const [loadingCompleted, setLoadingCompleted] = useState(false);
    const [searchCompleted, setSearchCompleted] = useState('');
    const [filterFrom, setFilterFrom] = useState('');
    const [filterTo, setFilterTo] = useState('');
    const [filterDateFrom, setFilterDateFrom] = useState('');
    const [filterDateTo, setFilterDateTo] = useState('');
    const [currentCompletedPage, setCurrentCompletedPage] = useState(1);
    const [totalCompletedPages, setTotalCompletedPages] = useState(1);

    // Modals
    const [showVerifyModal, setShowVerifyModal] = useState(false);
    const [selectedEntry, setSelectedEntry] = useState(null);
    const [showSignatureModal, setShowSignatureModal] = useState(false);
    const [signatureType, setSignatureType] = useState(''); // 'from' or 'to'
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showReceiverModal, setShowReceiverModal] = useState(false);
    const [receiverFormData, setReceiverFormData] = useState({
        receiver: '',
        receiverType: 'user',
        receiverUserId: ''
    });
    const [editFormData, setEditFormData] = useState({
        from: '',
        fromType: 'user',
        fromUserId: '',
        to: '',
        toType: 'user',
        toUserId: '',
        receiver: '',
        receiverType: 'user',
        receiverUserId: '',
        documents: '',
        additionalVerifier1: '',
        additionalVerifier2: ''
    });

    // Signature Canvas: get coordinates in canvas space (works for mouse + touch, scales to canvas internal size)
    const getCanvasCoords = (e) => {
        const canvas = canvasRef.current;
        if (!canvas) return null;
        const rect = canvas.getBoundingClientRect();
        const clientX = e.touches ? (e.touches[0]?.clientX ?? e.changedTouches?.[0]?.clientX) : e.clientX;
        const clientY = e.touches ? (e.touches[0]?.clientY ?? e.changedTouches?.[0]?.clientY) : e.clientY;
        if (clientX == null || clientY == null) return null;
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        return {
            x: (clientX - rect.left) * scaleX,
            y: (clientY - rect.top) * scaleY
        };
    };

    const startDrawing = (e) => {
        if (e.touches) e.preventDefault();
        const canvas = canvasRef.current;
        const coords = getCanvasCoords(e);
        if (!canvas || !coords) return;
        const ctx = canvas.getContext('2d');
        ctx.beginPath();
        ctx.moveTo(coords.x, coords.y);
        setIsDrawing(true);
    };

    const draw = (e) => {
        if (!isDrawing) return;
        if (e.touches) e.preventDefault();
        const canvas = canvasRef.current;
        const coords = getCanvasCoords(e);
        if (!canvas || !coords) return;
        const ctx = canvas.getContext('2d');
        ctx.lineTo(coords.x, coords.y);
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.stroke();
    };

    const stopDrawing = () => {
        setIsDrawing(false);
    };

    drawingHandlersRef.current = { startDrawing, draw, stopDrawing };

    // Non-passive touch listeners so preventDefault() works and page doesn't scroll while drawing on mobile
    useEffect(() => {
        if (!showSignatureModal) return;
        let canvas = null;
        let cleanup = null;
        const t = setTimeout(() => {
            canvas = canvasRef.current;
            if (!canvas) return;
            const onTouchStart = (e) => {
                e.preventDefault();
                drawingHandlersRef.current.startDrawing?.(e);
            };
            const onTouchMove = (e) => {
                e.preventDefault();
                drawingHandlersRef.current.draw?.(e);
            };
            const onTouchEnd = (e) => {
                e.preventDefault();
                drawingHandlersRef.current.stopDrawing?.(e);
            };
            canvas.addEventListener('touchstart', onTouchStart, { passive: false });
            canvas.addEventListener('touchmove', onTouchMove, { passive: false });
            canvas.addEventListener('touchend', onTouchEnd, { passive: false });
            canvas.addEventListener('touchcancel', onTouchEnd, { passive: false });
            cleanup = () => {
                if (canvas) {
                    canvas.removeEventListener('touchstart', onTouchStart);
                    canvas.removeEventListener('touchmove', onTouchMove);
                    canvas.removeEventListener('touchend', onTouchEnd);
                    canvas.removeEventListener('touchcancel', onTouchEnd);
                }
            };
        }, 0);
        return () => {
            clearTimeout(t);
            if (cleanup) cleanup();
        };
    }, [showSignatureModal]);

    const clearSignature = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        if (name === 'fromType') {
            setFormData(prev => ({
                ...prev,
                fromType: value,
                from: '',
                fromUserId: ''
            }));
        } else if (name === 'toType') {
            setFormData(prev => ({
                ...prev,
                toType: value,
                to: '',
                toUserId: ''
            }));
        } else if (name === 'receiverType') {
            setFormData(prev => ({
                ...prev,
                receiverType: value,
                receiver: '',
                receiverUserId: ''
            }));
        } else if (name === 'from' && formData.fromType === 'user') {
            const selectedUser = value;
            setFormData(prev => ({
                ...prev,
                from: selectedUser,
                fromUserId: selectedUser
            }));
        } else if (name === 'to' && formData.toType === 'user') {
            const selectedUser = value;
            setFormData(prev => ({
                ...prev,
                to: selectedUser,
                toUserId: selectedUser
            }));
        } else if (name === 'receiver' && formData.receiverType === 'user') {
            const selectedUser = value;
            setFormData(prev => ({
                ...prev,
                receiver: selectedUser,
                receiverUserId: selectedUser
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
        setError('');
    };

    const validateCreateForm = () => {
        if (!formData.from.trim()) {
            setError('From field is required');
            return false;
        }
        if (!formData.to.trim()) {
            setError('To field is required');
            return false;
        }
        if (!formData.documents.trim()) {
            setError('Documents description is required');
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateCreateForm()) {
            return;
        }

        setLoading(true);
        setError('');

        try {
            const additionalVerifiers = [];
            if (formData.additionalVerifier1) {
                const user1 = getUserById(formData.additionalVerifier1);
                if (user1) {
                    additionalVerifiers.push({
                        userId: formData.additionalVerifier1,
                        fullName: user1.fullName
                    });
                }
            }
            if (formData.additionalVerifier2) {
                const user2 = getUserById(formData.additionalVerifier2);
                if (user2) {
                    additionalVerifiers.push({
                        userId: formData.additionalVerifier2,
                        fullName: user2.fullName
                    });
                }
            }

            const payload = {
                from: formData.fromType === 'user' ? getUserById(formData.from)?.fullName : formData.from,
                fromUserId: formData.fromType === 'user' ? formData.from : null,
                to: formData.toType === 'user' ? getUserById(formData.to)?.fullName : formData.to,
                toUserId: formData.toType === 'user' ? formData.to : null,
                receiver: formData.receiver ? (formData.receiverType === 'user' ? getUserById(formData.receiver)?.fullName : formData.receiver) : null,
                receiverUserId: formData.receiverType === 'user' && formData.receiver ? formData.receiver : null,
                documents: formData.documents,
                additionalVerifiers
            };

            const response = await axiosInstance.post(
                '/api/document-log-book',
                payload
            );

            if (response.data.success) {
                setSuccess('Document log entry created successfully!');
                
                // Reset form for next entry
                setFormData({
                    from: '',
                    fromType: 'user',
                    fromUserId: '',
                    to: '',
                    toType: 'user',
                    toUserId: '',
                    receiver: '',
                    receiverType: 'user',
                    receiverUserId: '',
                    documents: '',
                    additionalVerifier1: '',
                    additionalVerifier2: ''
                });
                
                setTimeout(() => {
                    setSuccess('');
                }, 3000);
            }
        } catch (err) {
            console.error('Error submitting document log entry:', err);
            setError(err.response?.data?.message || 'Failed to submit document log entry. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const getUserById = (userId) => {
        for (const dept in users) {
            const user = users[dept].find(u => u._id === userId);
            if (user) return user;
        }
        return null;
    };

    const fetchUsers = async () => {
        setLoadingUsers(true);
        try {
            const response = await axiosInstance.get('/api/document-log-book/users');

            if (response.data.success) {
                setUsers(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching users:', err);
        } finally {
            setLoadingUsers(false);
        }
    };

    const fetchMyPendingEntries = async (page = 1, search = '') => {
        setLoadingMyPending(true);
        try {
            const response = await axiosInstance.get(
                `/api/document-log-book/pending/my?page=${page}&limit=10&search=${search}`
            );

            if (response.data.success) {
                setMyPendingEntries(response.data.data);
                setTotalMyPendingPages(response.data.totalPages);
                setCurrentMyPendingPage(response.data.currentPage);
            }
        } catch (err) {
            console.error('Error fetching my pending entries:', err);
            setError('Failed to load pending entries');
        } finally {
            setLoadingMyPending(false);
        }
    };

    const fetchAllPendingEntries = async (page = 1, search = '') => {
        setLoadingAllPending(true);
        try {
            const response = await axiosInstance.get(
                `/api/document-log-book/pending?page=${page}&limit=10&search=${search}`
            );

            if (response.data.success) {
                setAllPendingEntries(response.data.data);
                setTotalAllPendingPages(response.data.totalPages);
                setCurrentAllPendingPage(response.data.currentPage);
            }
        } catch (err) {
            console.error('Error fetching all pending entries:', err);
            setError('Failed to load pending entries');
        } finally {
            setLoadingAllPending(false);
        }
    };

    const fetchCompletedEntries = async (page = 1, search = '', filters = {}) => {
        setLoadingCompleted(true);
        try {
            const params = new URLSearchParams({ page, limit: 10, search });
            if (filters.from) params.append('from', filters.from);
            if (filters.to) params.append('to', filters.to);
            if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
            if (filters.dateTo) params.append('dateTo', filters.dateTo);

            const response = await axiosInstance.get(
                `/api/document-log-book/completed?${params.toString()}`
            );

            if (response.data.success) {
                setCompletedEntries(response.data.data);
                setTotalCompletedPages(response.data.totalPages);
                setCurrentCompletedPage(response.data.currentPage);
            }
        } catch (err) {
            console.error('Error fetching completed entries:', err);
            setError('Failed to load completed entries');
        } finally {
            setLoadingCompleted(false);
        }
    };

    useEffect(() => {
        fetchUsers();
        if (activeTab === 'myPending') {
            fetchMyPendingEntries(currentMyPendingPage, searchMyPending);
        } else if (activeTab === 'allPending') {
            fetchAllPendingEntries(currentAllPendingPage, searchAllPending);
        } else if (activeTab === 'completed') {
            fetchCompletedEntries(currentCompletedPage, searchCompleted, {
                from: filterFrom,
                to: filterTo,
                dateFrom: filterDateFrom,
                dateTo: filterDateTo
            });
        }
    }, [activeTab]);

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchMyPendingEntries(1, searchMyPending);
            setCurrentMyPendingPage(1);
        }, 400);
        return () => clearTimeout(timer);
    }, [searchMyPending]);

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchAllPendingEntries(1, searchAllPending);
            setCurrentAllPendingPage(1);
        }, 400);
        return () => clearTimeout(timer);
    }, [searchAllPending]);

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchCompletedEntries(1, searchCompleted, {
                from: filterFrom,
                to: filterTo,
                dateFrom: filterDateFrom,
                dateTo: filterDateTo
            });
            setCurrentCompletedPage(1);
        }, 400);
        return () => clearTimeout(timer);
    }, [searchCompleted, filterFrom, filterTo, filterDateFrom, filterDateTo]);

    const handleVerify = (entry) => {
        setSelectedEntry(entry);
        setShowVerifyModal(true);
        setError('');
    };

    const handleCloseVerifyModal = () => {
        setShowVerifyModal(false);
        setSelectedEntry(null);
        setError('');
    };

    const handleConfirmVerify = async () => {
        setLoading(true);
        setError('');

        try {
            const response = await axiosInstance.post(
                `/api/document-log-book/${selectedEntry._id}/verify`,
                {}
            );

            if (response.data.success) {
                setSuccess('Entry verified successfully!');
                handleCloseVerifyModal();
                fetchMyPendingEntries(currentMyPendingPage, searchMyPending);
                if (hasFullAccess) {
                    fetchAllPendingEntries(currentAllPendingPage, searchAllPending);
                }
                
                setTimeout(() => {
                    setSuccess('');
                }, 3000);
            }
        } catch (err) {
            console.error('Error verifying entry:', err);
            setError(err.response?.data?.message || 'Failed to verify entry. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleAddSignature = (entry, type) => {
        setSelectedEntry(entry);
        setSignatureType(type);
        setShowSignatureModal(true);
        setError('');
        
        setTimeout(() => {
            if (canvasRef.current) {
                const canvas = canvasRef.current;
                const ctx = canvas.getContext('2d');
                ctx.clearRect(0, 0, canvas.width, canvas.height);
            }
        }, 100);
    };

    const handleCloseSignatureModal = () => {
        setShowSignatureModal(false);
        setSelectedEntry(null);
        setSignatureType('');
        setError('');
    };

    const handleSubmitSignature = async () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        
        let isEmpty = true;
        for (let i = 0; i < data.length; i += 4) {
            if (data[i + 3] !== 0) {
                isEmpty = false;
                break;
            }
        }

        if (isEmpty) {
            setError('Please draw a signature');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const signatureBase64 = canvas.toDataURL('image/png');

            const endpoint = signatureType === 'from' 
                ? `/api/document-log-book/${selectedEntry._id}/from-signature`
                : `/api/document-log-book/${selectedEntry._id}/to-signature`;

            const response = await axiosInstance.post(
                endpoint,
                { signature: signatureBase64 }
            );

            if (response.data.success) {
                setSuccess(`${signatureType === 'from' ? 'From' : 'To'} signature added successfully!`);
                handleCloseSignatureModal();
                fetchAllPendingEntries(currentAllPendingPage, searchAllPending);
                
                setTimeout(() => {
                    setSuccess('');
                }, 3000);
            }
        } catch (err) {
            console.error('Error adding signature:', err);
            setError(err.response?.data?.message || 'Failed to add signature. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleViewDetails = (entry) => {
        setSelectedEntry(entry);
        setShowDetailsModal(true);
    };

    const handleCloseDetailsModal = () => {
        setShowDetailsModal(false);
        setSelectedEntry(null);
    };

    const handleEdit = (entry) => {
        setSelectedEntry(entry);
        
        // Populate edit form
        setEditFormData({
            from: entry.fromUserId || entry.from,
            fromType: entry.fromUserId ? 'user' : 'other',
            fromUserId: entry.fromUserId || '',
            to: entry.toUserId || entry.to,
            toType: entry.toUserId ? 'user' : 'other',
            toUserId: entry.toUserId || '',
            receiver: entry.receiverUserId || entry.receiver || '',
            receiverType: entry.receiverUserId ? 'user' : 'other',
            receiverUserId: entry.receiverUserId || '',
            documents: entry.documents,
            additionalVerifier1: entry.additionalVerifiers?.[0]?.userId || '',
            additionalVerifier2: entry.additionalVerifiers?.[1]?.userId || ''
        });
        
        setShowEditModal(true);
        setError('');
    };

    const handleCloseEditModal = () => {
        setShowEditModal(false);
        setSelectedEntry(null);
        setEditFormData({
            from: '',
            fromType: 'user',
            fromUserId: '',
            to: '',
            toType: 'user',
            toUserId: '',
            receiver: '',
            receiverType: 'user',
            receiverUserId: '',
            documents: '',
            additionalVerifier1: '',
            additionalVerifier2: ''
        });
        setError('');
    };

    const handleUpdateReceiver = (entry) => {
        setSelectedEntry(entry);
        setReceiverFormData({
            receiver: entry.receiverUserId || entry.receiver || '',
            receiverType: entry.receiverUserId ? 'user' : 'other',
            receiverUserId: entry.receiverUserId || ''
        });
        setShowReceiverModal(true);
        setError('');
    };

    const handleCloseReceiverModal = () => {
        setShowReceiverModal(false);
        setSelectedEntry(null);
        setReceiverFormData({
            receiver: '',
            receiverType: 'user',
            receiverUserId: ''
        });
        setError('');
    };

    const handleReceiverInputChange = (e) => {
        const { name, value } = e.target;
        
        if (name === 'receiverType') {
            setReceiverFormData(prev => ({
                ...prev,
                receiverType: value,
                receiver: '',
                receiverUserId: ''
            }));
        } else if (name === 'receiver' && receiverFormData.receiverType === 'user') {
            setReceiverFormData(prev => ({
                ...prev,
                receiver: value,
                receiverUserId: value
            }));
        } else {
            setReceiverFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
        setError('');
    };

    const handleSubmitReceiver = async () => {
        setLoading(true);
        setError('');

        try {
            const payload = {
                from: selectedEntry.from,
                fromUserId: selectedEntry.fromUserId,
                to: selectedEntry.to,
                toUserId: selectedEntry.toUserId,
                receiver: receiverFormData.receiver ? (receiverFormData.receiverType === 'user' ? getUserById(receiverFormData.receiver)?.fullName : receiverFormData.receiver) : null,
                receiverUserId: receiverFormData.receiverType === 'user' && receiverFormData.receiver ? receiverFormData.receiver : null,
                documents: selectedEntry.documents,
                additionalVerifiers: selectedEntry.additionalVerifiers
            };

            const response = await axiosInstance.patch(
                `/api/document-log-book/${selectedEntry._id}`,
                payload
            );

            if (response.data.success) {
                setSuccess('Final receiver updated successfully!');
                handleCloseReceiverModal();
                fetchCompletedEntries(currentCompletedPage, searchCompleted, {
                    from: filterFrom,
                    to: filterTo,
                    dateFrom: filterDateFrom,
                    dateTo: filterDateTo
                });
                
                setTimeout(() => {
                    setSuccess('');
                }, 3000);
            }
        } catch (err) {
            console.error('Error updating receiver:', err);
            setError(err.response?.data?.message || 'Failed to update receiver. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleEditInputChange = (e) => {
        const { name, value } = e.target;
        
        if (name === 'fromType') {
            setEditFormData(prev => ({
                ...prev,
                fromType: value,
                from: '',
                fromUserId: ''
            }));
        } else if (name === 'toType') {
            setEditFormData(prev => ({
                ...prev,
                toType: value,
                to: '',
                toUserId: ''
            }));
        } else if (name === 'receiverType') {
            setEditFormData(prev => ({
                ...prev,
                receiverType: value,
                receiver: '',
                receiverUserId: ''
            }));
        } else if (name === 'from' && editFormData.fromType === 'user') {
            setEditFormData(prev => ({
                ...prev,
                from: value,
                fromUserId: value
            }));
        } else if (name === 'to' && editFormData.toType === 'user') {
            setEditFormData(prev => ({
                ...prev,
                to: value,
                toUserId: value
            }));
        } else if (name === 'receiver' && editFormData.receiverType === 'user') {
            setEditFormData(prev => ({
                ...prev,
                receiver: value,
                receiverUserId: value
            }));
        } else {
            setEditFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
        setError('');
    };

    const handleSubmitEdit = async () => {
        if (!editFormData.from.trim()) {
            setError('From field is required');
            return;
        }
        if (!editFormData.to.trim()) {
            setError('To field is required');
            return;
        }
        if (!editFormData.documents.trim()) {
            setError('Documents description is required');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const additionalVerifiers = [];
            if (editFormData.additionalVerifier1) {
                const user1 = getUserById(editFormData.additionalVerifier1);
                if (user1) {
                    additionalVerifiers.push({
                        userId: editFormData.additionalVerifier1,
                        fullName: user1.fullName
                    });
                }
            }
            if (editFormData.additionalVerifier2) {
                const user2 = getUserById(editFormData.additionalVerifier2);
                if (user2) {
                    additionalVerifiers.push({
                        userId: editFormData.additionalVerifier2,
                        fullName: user2.fullName
                    });
                }
            }

            const payload = {
                from: editFormData.fromType === 'user' ? getUserById(editFormData.from)?.fullName : editFormData.from,
                fromUserId: editFormData.fromType === 'user' ? editFormData.from : null,
                to: editFormData.toType === 'user' ? getUserById(editFormData.to)?.fullName : editFormData.to,
                toUserId: editFormData.toType === 'user' ? editFormData.to : null,
                receiver: editFormData.receiver ? (editFormData.receiverType === 'user' ? getUserById(editFormData.receiver)?.fullName : editFormData.receiver) : null,
                receiverUserId: editFormData.receiverType === 'user' && editFormData.receiver ? editFormData.receiver : null,
                documents: editFormData.documents,
                additionalVerifiers
            };

            const response = await axiosInstance.patch(
                `/api/document-log-book/${selectedEntry._id}`,
                payload
            );

            if (response.data.success) {
                setSuccess('Document updated successfully!');
                handleCloseEditModal();
                fetchAllPendingEntries(currentAllPendingPage, searchAllPending);
                
                setTimeout(() => {
                    setSuccess('');
                }, 3000);
            }
        } catch (err) {
            console.error('Error updating document:', err);
            setError(err.response?.data?.message || 'Failed to update document. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusBadge = (entry) => {
        if (entry.status === 'FULLY_VERIFIED') {
            return <span className="status-badge status-completed">Fully Verified</span>;
        } else if (entry.status === 'PARTIALLY_VERIFIED') {
            return <span className="status-badge status-partial">Partially Verified</span>;
        } else {
            return <span className="status-badge status-pending">Pending</span>;
        }
    };

    const needsFromSignature = (entry) => {
        return !entry.fromUserId && (!entry.fromSignature || !entry.fromSignature.signature);
    };

    const needsToSignature = (entry) => {
        return !entry.toUserId && (!entry.toSignature || !entry.toSignature.signature);
    };

    return (
        <div className="document-log-book-container" style={{ backgroundImage: `url(${receptionBg})` }}>
            <div className="document-log-book-content">
                <div className="document-log-book-header">
                    <img src="/logo.png" alt="Logo" className="app-logo" />
                    <div className="header-text">
                        <h1>Document Log Book</h1>
                        <p>Document verification and tracking system</p>
                    </div>
                    <div className="header-actions">
                        <div className="user-info">
                            <strong>{user?.fullName || user?.username}</strong>
                        </div>
                        <button onClick={() => navigate('/purpose-selection')} className="btn-back">
                            ← Back
                        </button>
                    </div>
                </div>

                <div className="document-log-book-tabs">
                    {hasFullAccess && (
                        <button
                            className={`tab-button ${activeTab === 'create' ? 'active' : ''}`}
                            onClick={() => setActiveTab('create')}
                        >
                            Create Entry
                        </button>
                    )}
                    <button
                        className={`tab-button ${activeTab === 'myPending' ? 'active' : ''}`}
                        onClick={() => setActiveTab('myPending')}
                    >
                        My Pending Verification
                    </button>
                    {hasFullAccess && (
                        <button
                            className={`tab-button ${activeTab === 'allPending' ? 'active' : ''}`}
                            onClick={() => setActiveTab('allPending')}
                        >
                            All Pending
                        </button>
                    )}
                    <button
                        className={`tab-button ${activeTab === 'completed' ? 'active' : ''}`}
                        onClick={() => setActiveTab('completed')}
                    >
                        Completed
                    </button>
                </div>

                {success && (
                    <div className="alert alert-success">
                        {success}
                    </div>
                )}

                {error && !showVerifyModal && !showSignatureModal && (
                    <div className="alert alert-error">
                        {error}
                    </div>
                )}

                {activeTab === 'create' && hasFullAccess && (
                    <form onSubmit={handleSubmit} className="document-log-book-form">
                        <div className="form-grid">
                            <div className="form-group">
                                <label htmlFor="from">From *</label>
                                <div className="input-with-toggle">
                                    <select
                                        name="fromType"
                                        value={formData.fromType}
                                        onChange={handleInputChange}
                                        disabled={loading}
                                        className="type-selector"
                                    >
                                        <option value="user">Select User</option>
                                        <option value="other">Other (Custom)</option>
                                    </select>
                                    
                                    {formData.fromType === 'user' ? (
                                        <select
                                            id="from"
                                            name="from"
                                            value={formData.from}
                                            onChange={handleInputChange}
                                            disabled={loading || loadingUsers}
                                            required
                                            className="user-dropdown"
                                        >
                                            <option value="">-- Select User --</option>
                                            {Object.keys(users).sort().map(department => (
                                                <optgroup key={department} label={department}>
                                                    {users[department].map(user => (
                                                        <option key={user._id} value={user._id}>
                                                            {user.fullName}
                                                        </option>
                                                    ))}
                                                </optgroup>
                                            ))}
                                        </select>
                                    ) : (
                                        <input
                                            type="text"
                                            id="from"
                                            name="from"
                                            value={formData.from}
                                            onChange={handleInputChange}
                                            disabled={loading}
                                            placeholder="Enter custom name (e.g., DHL Courier)"
                                            required
                                        />
                                    )}
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="to">To *</label>
                                <div className="input-with-toggle">
                                    <select
                                        name="toType"
                                        value={formData.toType}
                                        onChange={handleInputChange}
                                        disabled={loading}
                                        className="type-selector"
                                    >
                                        <option value="user">Select User</option>
                                        <option value="other">Other (Custom)</option>
                                    </select>
                                    
                                    {formData.toType === 'user' ? (
                                        <select
                                            id="to"
                                            name="to"
                                            value={formData.to}
                                            onChange={handleInputChange}
                                            disabled={loading || loadingUsers}
                                            required
                                            className="user-dropdown"
                                        >
                                            <option value="">-- Select User --</option>
                                            {Object.keys(users).sort().map(department => (
                                                <optgroup key={department} label={department}>
                                                    {users[department].map(user => (
                                                        <option key={user._id} value={user._id}>
                                                            {user.fullName}
                                                        </option>
                                                    ))}
                                                </optgroup>
                                            ))}
                                        </select>
                                    ) : (
                                        <input
                                            type="text"
                                            id="to"
                                            name="to"
                                            value={formData.to}
                                            onChange={handleInputChange}
                                            disabled={loading}
                                            placeholder="Enter custom name (e.g., Visitor John)"
                                            required
                                        />
                                    )}
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="receiver">Receiver (Optional - if different from To)</label>
                                <div className="input-with-toggle">
                                    <select
                                        name="receiverType"
                                        value={formData.receiverType}
                                        onChange={handleInputChange}
                                        disabled={loading}
                                        className="type-selector"
                                    >
                                        <option value="user">Select User</option>
                                        <option value="other">Other (Custom)</option>
                                    </select>
                                    
                                    {formData.receiverType === 'user' ? (
                                        <select
                                            id="receiver"
                                            name="receiver"
                                            value={formData.receiver}
                                            onChange={handleInputChange}
                                            disabled={loading || loadingUsers}
                                            className="user-dropdown"
                                        >
                                            <option value="">-- None --</option>
                                            {Object.keys(users).sort().map(department => (
                                                <optgroup key={department} label={department}>
                                                    {users[department].map(user => (
                                                        <option key={user._id} value={user._id}>
                                                            {user.fullName}
                                                        </option>
                                                    ))}
                                                </optgroup>
                                            ))}
                                        </select>
                                    ) : (
                                        <input
                                            type="text"
                                            id="receiver"
                                            name="receiver"
                                            value={formData.receiver}
                                            onChange={handleInputChange}
                                            disabled={loading}
                                            placeholder="Enter actual receiver name"
                                        />
                                    )}
                                </div>
                            </div>

                            <div className="form-group form-group-full">
                                <label htmlFor="documents">Documents *</label>
                                <textarea
                                    id="documents"
                                    name="documents"
                                    value={formData.documents}
                                    onChange={handleInputChange}
                                    disabled={loading}
                                    placeholder="Description or list of documents handed over"
                                    rows="4"
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="additionalVerifier1">Additional Verifier 1 (Optional)</label>
                                <select
                                    id="additionalVerifier1"
                                    name="additionalVerifier1"
                                    value={formData.additionalVerifier1}
                                    onChange={handleInputChange}
                                    disabled={loading || loadingUsers}
                                    className="user-dropdown"
                                >
                                    <option value="">-- None --</option>
                                    {Object.keys(users).sort().map(department => (
                                        <optgroup key={department} label={department}>
                                            {users[department].map(user => (
                                                <option key={user._id} value={user._id}>
                                                    {user.fullName}
                                                </option>
                                            ))}
                                        </optgroup>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label htmlFor="additionalVerifier2">Additional Verifier 2 (Optional)</label>
                                <select
                                    id="additionalVerifier2"
                                    name="additionalVerifier2"
                                    value={formData.additionalVerifier2}
                                    onChange={handleInputChange}
                                    disabled={loading || loadingUsers}
                                    className="user-dropdown"
                                >
                                    <option value="">-- None --</option>
                                    {Object.keys(users).sort().map(department => (
                                        <optgroup key={department} label={department}>
                                            {users[department].map(user => (
                                                <option key={user._id} value={user._id}>
                                                    {user.fullName}
                                                </option>
                                            ))}
                                        </optgroup>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="info-note">
                            <span className="info-icon">ℹ️</span>
                            <p>System users will verify digitally. Custom users will need signature collection by receptionist.</p>
                        </div>

                        <div className="form-actions">
                            <button
                                type="button"
                                onClick={() => navigate('/purpose-selection')}
                                className="btn btn-secondary"
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={loading}
                            >
                                {loading ? 'Creating...' : 'Create Entry'}
                            </button>
                        </div>
                    </form>
                )}

                {activeTab === 'myPending' && (
                    <div className="view-all-section">
                        <div className="search-bar">
                            <input
                                type="text"
                                placeholder="Search my pending entries..."
                                value={searchMyPending}
                                onChange={(e) => setSearchMyPending(e.target.value)}
                                className="search-input"
                            />
                        </div>

                        {loadingMyPending ? (
                            <div className="loading-state">Loading pending entries...</div>
                        ) : myPendingEntries.length === 0 ? (
                            <div className="empty-state">
                                <p>No pending verification found</p>
                                <p className="empty-state-hint">You have no documents waiting for your verification.</p>
                            </div>
                        ) : (
                            <>
                                <div className="entries-table-container">
                                    <table className="entries-table">
                                        <thead>
                                            <tr>
                                                <th>Actions</th>
                                                <th>ID</th>
                                                <th>Entry Date</th>
                                                <th>From</th>
                                                <th>To</th>
                                                <th>Receiver</th>
                                                <th>Documents</th>
                                                <th>Entered By</th>
                                                <th>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {myPendingEntries.map((entry) => (
                                                <tr key={entry._id}>
                                                    <td>
                                                        <button
                                                            className="btn-verify"
                                                            onClick={() => handleVerify(entry)}
                                                        >
                                                            Verify
                                                        </button>
                                                    </td>
                                                    <td>{entry.logId || '—'}</td>
                                                    <td>{formatDateTime(entry.entryDate)}</td>
                                                    <td>{entry.from}</td>
                                                    <td>{entry.to}</td>
                                                    <td>{entry.receiver || '-'}</td>
                                                    <td className="documents-cell">
                                                        <div className="documents-cell-inner">
                                                        {entry.documents && entry.documents.length > DOC_TRUNCATE_LEN ? (
                                                            <>
                                                                <span>{expandedDocs[entry._id]
                                                                    ? entry.documents
                                                                    : entry.documents.slice(0, DOC_TRUNCATE_LEN) + '...'}</span>
                                                                <button
                                                                    className="see-more-btn"
                                                                    onClick={() => toggleDocExpand(entry._id)}
                                                                >
                                                                    {expandedDocs[entry._id] ? 'See less' : 'See more'}
                                                                </button>
                                                            </>
                                                        ) : (
                                                            entry.documents
                                                        )}
                                                        </div>
                                                    </td>
                                                    <td>{entry.enteredBy}</td>
                                                    <td>{getStatusBadge(entry)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {totalMyPendingPages > 1 && (
                                    <div className="pagination">
                                        <button
                                            onClick={() => {
                                                const newPage = currentMyPendingPage - 1;
                                                setCurrentMyPendingPage(newPage);
                                                fetchMyPendingEntries(newPage, searchMyPending);
                                            }}
                                            disabled={currentMyPendingPage === 1}
                                            className="pagination-btn"
                                        >
                                            Previous
                                        </button>
                                        <span className="pagination-info">
                                            Page {currentMyPendingPage} of {totalMyPendingPages}
                                        </span>
                                        <button
                                            onClick={() => {
                                                const newPage = currentMyPendingPage + 1;
                                                setCurrentMyPendingPage(newPage);
                                                fetchMyPendingEntries(newPage, searchMyPending);
                                            }}
                                            disabled={currentMyPendingPage === totalMyPendingPages}
                                            className="pagination-btn"
                                        >
                                            Next
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}

                {activeTab === 'allPending' && hasFullAccess && (
                    <div className="view-all-section">
                        <div className="search-bar">
                            <input
                                type="text"
                                placeholder="Search all pending entries..."
                                value={searchAllPending}
                                onChange={(e) => setSearchAllPending(e.target.value)}
                                className="search-input"
                            />
                        </div>

                        {loadingAllPending ? (
                            <div className="loading-state">Loading pending entries...</div>
                        ) : allPendingEntries.length === 0 ? (
                            <div className="empty-state">
                                <p>No pending entries found</p>
                            </div>
                        ) : (
                            <>
                                <div className="entries-table-container">
                                    <table className="entries-table">
                                        <thead>
                                            <tr>
                                                <th>Actions</th>
                                                <th>ID</th>
                                                <th>Documents</th>
                                                <th>From</th>
                                                <th>To</th>
                                                <th>Receiver</th>
                                                <th>Status</th>
                                                <th>Entry Date</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {allPendingEntries.map((entry) => (
                                                <tr key={entry._id}>
                                                    <td>
                                                        <div className="action-buttons">
                                                            {entry.status === 'PENDING_VERIFICATION' && (
                                                                <button
                                                                    className="btn-edit"
                                                                    onClick={() => handleEdit(entry)}
                                                                    title="Edit document details"
                                                                >
                                                                    Edit
                                                                </button>
                                                            )}
                                                            {needsFromSignature(entry) && (
                                                                <button
                                                                    className="btn-add-signature"
                                                                    onClick={() => handleAddSignature(entry, 'from')}
                                                                    title="Collect signature from 'From' party"
                                                                >
                                                                    From Sign
                                                                </button>
                                                            )}
                                                            {needsToSignature(entry) && (
                                                                <button
                                                                    className="btn-add-signature"
                                                                    onClick={() => handleAddSignature(entry, 'to')}
                                                                    title="Collect signature from 'To' party"
                                                                >
                                                                    To Sign
                                                                </button>
                                                            )}
                                                            <button
                                                                className="btn-view-details"
                                                                onClick={() => handleViewDetails(entry)}
                                                            >
                                                                View
                                                            </button>
                                                        </div>
                                                    </td>
                                                    <td>{entry.logId || '—'}</td>
                                                    <td className="documents-cell">
                                                        <div className="documents-cell-inner">
                                                        {entry.documents && entry.documents.length > DOC_TRUNCATE_LEN ? (
                                                            <>
                                                                <span>{expandedDocs[entry._id]
                                                                    ? entry.documents
                                                                    : entry.documents.slice(0, DOC_TRUNCATE_LEN) + '...'}</span>
                                                                <button
                                                                    className="see-more-btn"
                                                                    onClick={() => toggleDocExpand(entry._id)}
                                                                >
                                                                    {expandedDocs[entry._id] ? 'See less' : 'See more'}
                                                                </button>
                                                            </>
                                                        ) : (
                                                            entry.documents
                                                        )}
                                                        </div>
                                                    </td>
                                                    <td>{entry.from}</td>
                                                    <td>{entry.to}</td>
                                                    <td>{entry.receiver || '-'}</td>
                                                    <td>{getStatusBadge(entry)}</td>
                                                    <td>{formatDateTime(entry.entryDate)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {totalAllPendingPages > 1 && (
                                    <div className="pagination">
                                        <button
                                            onClick={() => {
                                                const newPage = currentAllPendingPage - 1;
                                                setCurrentAllPendingPage(newPage);
                                                fetchAllPendingEntries(newPage, searchAllPending);
                                            }}
                                            disabled={currentAllPendingPage === 1}
                                            className="pagination-btn"
                                        >
                                            Previous
                                        </button>
                                        <span className="pagination-info">
                                            Page {currentAllPendingPage} of {totalAllPendingPages}
                                        </span>
                                        <button
                                            onClick={() => {
                                                const newPage = currentAllPendingPage + 1;
                                                setCurrentAllPendingPage(newPage);
                                                fetchAllPendingEntries(newPage, searchAllPending);
                                            }}
                                            disabled={currentAllPendingPage === totalAllPendingPages}
                                            className="pagination-btn"
                                        >
                                            Next
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}

                {activeTab === 'completed' && (
                    <div className="view-all-section">
                        <div className="search-bar">
                            <input
                                type="text"
                                placeholder="Search completed entries..."
                                value={searchCompleted}
                                onChange={(e) => setSearchCompleted(e.target.value)}
                                className="search-input"
                            />
                        </div>

                        <div className="filter-bar">
                            <div className="filter-field">
                                <label>From</label>
                                <input
                                    type="text"
                                    placeholder="Filter by sender..."
                                    value={filterFrom}
                                    onChange={(e) => setFilterFrom(e.target.value)}
                                    className="filter-input"
                                />
                            </div>
                            <div className="filter-field">
                                <label>To</label>
                                <input
                                    type="text"
                                    placeholder="Filter by recipient..."
                                    value={filterTo}
                                    onChange={(e) => setFilterTo(e.target.value)}
                                    className="filter-input"
                                />
                            </div>
                            <div className="filter-field">
                                <label>Entry Date From</label>
                                <input
                                    type="date"
                                    value={filterDateFrom}
                                    onChange={(e) => setFilterDateFrom(e.target.value)}
                                    className="filter-input"
                                />
                            </div>
                            <div className="filter-field">
                                <label>Entry Date To</label>
                                <input
                                    type="date"
                                    value={filterDateTo}
                                    onChange={(e) => setFilterDateTo(e.target.value)}
                                    className="filter-input"
                                />
                            </div>
                            {(filterFrom || filterTo || filterDateFrom || filterDateTo) && (
                                <button
                                    type="button"
                                    className="btn-clear-filters"
                                    onClick={() => {
                                        setFilterFrom('');
                                        setFilterTo('');
                                        setFilterDateFrom('');
                                        setFilterDateTo('');
                                    }}
                                >
                                    Clear Filters
                                </button>
                            )}
                        </div>

                        {loadingCompleted ? (
                            <div className="loading-state">Loading completed entries...</div>
                        ) : completedEntries.length === 0 ? (
                            <div className="empty-state">
                                <p>No completed entries found</p>
                            </div>
                        ) : (
                            <>
                                <div className="entries-table-container">
                                    <table className="entries-table">
                                        <thead>
                                            <tr>
                                                <th style={{ width: '80px' }}>Actions</th>
                                                <th>ID</th>
                                                <th>Documents</th>
                                                <th>From</th>
                                                <th>To</th>
                                                <th>Final Receiver</th>
                                                <th>Entry Date</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {completedEntries.map((entry) => (
                                                <tr key={entry._id}>
                                                    <td style={{ verticalAlign: 'middle' }}>
                                                        <div className="action-buttons" style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
                                                            <button
                                                                className="btn-icon"
                                                                onClick={() => handleUpdateReceiver(entry)}
                                                                title="Update final receiver"
                                                                style={{ fontSize: '18px', padding: '4px 8px' }}
                                                            >
                                                                👤
                                                            </button>
                                                            <button
                                                                className="btn-icon"
                                                                onClick={() => handleViewDetails(entry)}
                                                                title="View details"
                                                                style={{ fontSize: '18px', padding: '4px 8px' }}
                                                            >
                                                                👁️
                                                            </button>
                                                        </div>
                                                    </td>
                                                    <td>{entry.logId || '—'}</td>
                                                    <td className="documents-cell">{entry.documents}</td>
                                                    <td>{entry.from}</td>
                                                    <td>{entry.to}</td>
                                                    <td>{entry.receiver || '-'}</td>
                                                    <td>{formatDateTime(entry.entryDate)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {totalCompletedPages > 1 && (
                                    <div className="pagination">
                                        <button
                                            onClick={() => {
                                                const newPage = currentCompletedPage - 1;
                                                setCurrentCompletedPage(newPage);
                                                fetchCompletedEntries(newPage, searchCompleted, {
                                                    from: filterFrom,
                                                    to: filterTo,
                                                    dateFrom: filterDateFrom,
                                                    dateTo: filterDateTo
                                                });
                                            }}
                                            disabled={currentCompletedPage === 1}
                                            className="pagination-btn"
                                        >
                                            Previous
                                        </button>
                                        <span className="pagination-info">
                                            Page {currentCompletedPage} of {totalCompletedPages}
                                        </span>
                                        <button
                                            onClick={() => {
                                                const newPage = currentCompletedPage + 1;
                                                setCurrentCompletedPage(newPage);
                                                fetchCompletedEntries(newPage, searchCompleted, {
                                                    from: filterFrom,
                                                    to: filterTo,
                                                    dateFrom: filterDateFrom,
                                                    dateTo: filterDateTo
                                                });
                                            }}
                                            disabled={currentCompletedPage === totalCompletedPages}
                                            className="pagination-btn"
                                        >
                                            Next
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}

                {showVerifyModal && selectedEntry && (
                    <div className="modal-overlay" onClick={handleCloseVerifyModal}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2>Verify Document Entry</h2>
                                <button className="modal-close" onClick={handleCloseVerifyModal}>×</button>
                            </div>
                            <div className="modal-body">
                                {error && (
                                    <div className="alert alert-error">
                                        {error}
                                    </div>
                                )}

                                <div className="entry-details-readonly">
                                    <div className="detail-row">
                                        <label>From:</label>
                                        <span>{selectedEntry.from}</span>
                                    </div>
                                    <div className="detail-row">
                                        <label>To:</label>
                                        <span>{selectedEntry.to}</span>
                                    </div>
                                    <div className="detail-row">
                                        <label>Documents:</label>
                                        <span>{selectedEntry.documents}</span>
                                    </div>
                                    <div className="detail-row">
                                        <label>Entry Date:</label>
                                        <span>{formatDateTime(selectedEntry.entryDate)}</span>
                                    </div>
                                    <div className="detail-row">
                                        <label>Entered By:</label>
                                        <span>{selectedEntry.enteredBy}</span>
                                    </div>
                                </div>

                                <div className="verification-confirm">
                                    <p>By clicking "Confirm Verification", you acknowledge that you have reviewed this document entry and confirm its accuracy.</p>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button 
                                    className="btn btn-secondary" 
                                    onClick={handleCloseVerifyModal}
                                    disabled={loading}
                                >
                                    Cancel
                                </button>
                                <button 
                                    className="btn btn-primary" 
                                    onClick={handleConfirmVerify}
                                    disabled={loading}
                                >
                                    {loading ? 'Verifying...' : 'Confirm Verification'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {showSignatureModal && selectedEntry && (
                    <div className="modal-overlay" onClick={handleCloseSignatureModal}>
                        <div className="modal-content signature-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2>Collect {signatureType === 'from' ? 'From' : 'To'} Signature</h2>
                                <button className="modal-close" onClick={handleCloseSignatureModal}>×</button>
                            </div>
                            <div className="modal-body">
                                {error && (
                                    <div className="alert alert-error">
                                        {error}
                                    </div>
                                )}

                                <div className="entry-details-readonly">
                                    <div className="detail-row">
                                        <label>{signatureType === 'from' ? 'From' : 'To'}:</label>
                                        <span>{signatureType === 'from' ? selectedEntry.from : selectedEntry.to}</span>
                                    </div>
                                    <div className="detail-row">
                                        <label>Documents:</label>
                                        <span>{selectedEntry.documents}</span>
                                    </div>
                                </div>

                                <div className="signature-section">
                                    <label>Draw Signature:</label>
                                    <div className="signature-canvas-row">
                                        <canvas
                                            ref={canvasRef}
                                            width={500}
                                            height={200}
                                            className="signature-canvas"
                                            onMouseDown={startDrawing}
                                            onMouseMove={draw}
                                            onMouseUp={stopDrawing}
                                            onMouseLeave={stopDrawing}
                                        />
                                        <button
                                            type="button"
                                            onClick={clearSignature}
                                            className="btn-clear-signature"
                                            title="Clear signature"
                                        >
                                            Clear signature
                                        </button>
                                    </div>
                                    <small className="form-hint">Draw the signature of the {signatureType === 'from' ? 'sender' : 'receiver'}</small>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button 
                                    className="btn btn-secondary" 
                                    onClick={handleCloseSignatureModal}
                                    disabled={loading}
                                >
                                    Cancel
                                </button>
                                <button 
                                    className="btn btn-primary" 
                                    onClick={handleSubmitSignature}
                                    disabled={loading}
                                >
                                    {loading ? 'Saving...' : 'Save Signature'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {showDetailsModal && selectedEntry && (
                    <div className="modal-overlay" onClick={handleCloseDetailsModal}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2>Document Log Entry Details</h2>
                                <button className="modal-close" onClick={handleCloseDetailsModal}>×</button>
                            </div>
                            <div className="modal-body">
                                <div className="detail-grid">
                                    <div className="detail-item">
                                        <label>Entry Date:</label>
                                        <span>{formatDateTime(selectedEntry.entryDate)}</span>
                                    </div>
                                    <div className="detail-item">
                                        <label>From:</label>
                                        <span>{selectedEntry.from}</span>
                                    </div>
                                    <div className="detail-item">
                                        <label>To:</label>
                                        <span>{selectedEntry.to}</span>
                                    </div>
                                    {selectedEntry.receiver && (
                                        <div className="detail-item">
                                            <label>Receiver:</label>
                                            <span>{selectedEntry.receiver}</span>
                                        </div>
                                    )}
                                    <div className="detail-item detail-item-full">
                                        <label>Documents:</label>
                                        <span>{selectedEntry.documents}</span>
                                    </div>
                                    <div className="detail-item">
                                        <label>Entered By:</label>
                                        <span>{selectedEntry.enteredBy}</span>
                                    </div>
                                    <div className="detail-item">
                                        <label>Status:</label>
                                        <span>{getStatusBadge(selectedEntry)}</span>
                                    </div>
                                    
                                    {selectedEntry.verifications && selectedEntry.verifications.length > 0 && (
                                        <div className="detail-item detail-item-full">
                                            <label>Verifications:</label>
                                            <div className="verifications-detail">
                                                {selectedEntry.verifications.map((v, idx) => (
                                                    <div key={idx} className="verification-detail-item">
                                                        <strong>{v.fullName}</strong> - {formatDateTime(v.verifiedAt)}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {selectedEntry.fromSignature && selectedEntry.fromSignature.signature && (
                                        <div className="detail-item detail-item-full">
                                            <label>From Signature:</label>
                                            <div className="signature-detail">
                                                <img 
                                                    src={selectedEntry.fromSignature.signature} 
                                                    alt="From Signature" 
                                                    className="signature-image"
                                                />
                                                <div><strong>Signed:</strong> {formatDateTime(selectedEntry.fromSignature.signedAt)}</div>
                                                <div><strong>Collected By:</strong> {selectedEntry.fromSignature.collectedBy}</div>
                                            </div>
                                        </div>
                                    )}

                                    {selectedEntry.toSignature && selectedEntry.toSignature.signature && (
                                        <div className="detail-item detail-item-full">
                                            <label>To Signature:</label>
                                            <div className="signature-detail">
                                                <img 
                                                    src={selectedEntry.toSignature.signature} 
                                                    alt="To Signature" 
                                                    className="signature-image"
                                                />
                                                <div><strong>Signed:</strong> {formatDateTime(selectedEntry.toSignature.signedAt)}</div>
                                                <div><strong>Collected By:</strong> {selectedEntry.toSignature.collectedBy}</div>
                                            </div>
                                        </div>
                                    )}

                                    {selectedEntry.externalSignature && selectedEntry.externalSignature.signerName && (
                                        <div className="detail-item detail-item-full">
                                            <label>External Signature (Legacy):</label>
                                            <div className="signature-detail">
                                                <div><strong>Signer:</strong> {selectedEntry.externalSignature.signerName}</div>
                                                <div><strong>Signature:</strong> {selectedEntry.externalSignature.signature}</div>
                                                <div><strong>Signed Date:</strong> {formatDateTime(selectedEntry.externalSignature.signedDate)}</div>
                                                <div><strong>Collected By:</strong> {selectedEntry.externalSignature.collectedBy}</div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={handleCloseDetailsModal}>
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {showEditModal && selectedEntry && (
                    <div className="modal-overlay" onClick={handleCloseEditModal}>
                        <div className="modal-content edit-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2>Edit Document Entry</h2>
                                <button className="modal-close" onClick={handleCloseEditModal}>×</button>
                            </div>
                            <div className="modal-body">
                                {error && (
                                    <div className="alert alert-error">
                                        {error}
                                    </div>
                                )}

                                <div className="form-grid">
                                    <div className="form-group">
                                        <label htmlFor="edit-from">From *</label>
                                        <div className="input-with-toggle">
                                            <select
                                                name="fromType"
                                                value={editFormData.fromType}
                                                onChange={handleEditInputChange}
                                                className="type-selector"
                                            >
                                                <option value="user">Select User</option>
                                                <option value="other">Other (Custom)</option>
                                            </select>
                                            
                                            {editFormData.fromType === 'user' ? (
                                                <select
                                                    id="edit-from"
                                                    name="from"
                                                    value={editFormData.from}
                                                    onChange={handleEditInputChange}
                                                    disabled={loadingUsers}
                                                    required
                                                    className="user-dropdown"
                                                >
                                                    <option value="">-- Select User --</option>
                                                    {Object.keys(users).sort().map(department => (
                                                        <optgroup key={department} label={department}>
                                                            {users[department].map(user => (
                                                                <option key={user._id} value={user._id}>
                                                                    {user.fullName}
                                                                </option>
                                                            ))}
                                                        </optgroup>
                                                    ))}
                                                </select>
                                            ) : (
                                                <input
                                                    type="text"
                                                    id="edit-from"
                                                    name="from"
                                                    value={editFormData.from}
                                                    onChange={handleEditInputChange}
                                                    placeholder="Enter custom name"
                                                    required
                                                />
                                            )}
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="edit-to">To *</label>
                                        <div className="input-with-toggle">
                                            <select
                                                name="toType"
                                                value={editFormData.toType}
                                                onChange={handleEditInputChange}
                                                className="type-selector"
                                            >
                                                <option value="user">Select User</option>
                                                <option value="other">Other (Custom)</option>
                                            </select>
                                            
                                            {editFormData.toType === 'user' ? (
                                                <select
                                                    id="edit-to"
                                                    name="to"
                                                    value={editFormData.to}
                                                    onChange={handleEditInputChange}
                                                    disabled={loadingUsers}
                                                    required
                                                    className="user-dropdown"
                                                >
                                                    <option value="">-- Select User --</option>
                                                    {Object.keys(users).sort().map(department => (
                                                        <optgroup key={department} label={department}>
                                                            {users[department].map(user => (
                                                                <option key={user._id} value={user._id}>
                                                                    {user.fullName}
                                                                </option>
                                                            ))}
                                                        </optgroup>
                                                    ))}
                                                </select>
                                            ) : (
                                                <input
                                                    type="text"
                                                    id="edit-to"
                                                    name="to"
                                                    value={editFormData.to}
                                                    onChange={handleEditInputChange}
                                                    placeholder="Enter custom name"
                                                    required
                                                />
                                            )}
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="edit-receiver">Receiver (Optional - if different from To)</label>
                                        <div className="input-with-toggle">
                                            <select
                                                name="receiverType"
                                                value={editFormData.receiverType}
                                                onChange={handleEditInputChange}
                                                className="type-selector"
                                            >
                                                <option value="user">Select User</option>
                                                <option value="other">Other (Custom)</option>
                                            </select>
                                            
                                            {editFormData.receiverType === 'user' ? (
                                                <select
                                                    id="edit-receiver"
                                                    name="receiver"
                                                    value={editFormData.receiver}
                                                    onChange={handleEditInputChange}
                                                    disabled={loadingUsers}
                                                    className="user-dropdown"
                                                >
                                                    <option value="">-- None --</option>
                                                    {Object.keys(users).sort().map(department => (
                                                        <optgroup key={department} label={department}>
                                                            {users[department].map(user => (
                                                                <option key={user._id} value={user._id}>
                                                                    {user.fullName}
                                                                </option>
                                                            ))}
                                                        </optgroup>
                                                    ))}
                                                </select>
                                            ) : (
                                                <input
                                                    type="text"
                                                    id="edit-receiver"
                                                    name="receiver"
                                                    value={editFormData.receiver}
                                                    onChange={handleEditInputChange}
                                                    placeholder="Enter actual receiver name"
                                                />
                                            )}
                                        </div>
                                    </div>

                                    <div className="form-group form-group-full">
                                        <label htmlFor="edit-documents">Documents *</label>
                                        <textarea
                                            id="edit-documents"
                                            name="documents"
                                            value={editFormData.documents}
                                            onChange={handleEditInputChange}
                                            placeholder="Description or list of documents"
                                            rows="4"
                                            required
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="edit-additionalVerifier1">Additional Verifier 1</label>
                                        <select
                                            id="edit-additionalVerifier1"
                                            name="additionalVerifier1"
                                            value={editFormData.additionalVerifier1}
                                            onChange={handleEditInputChange}
                                            disabled={loadingUsers}
                                            className="user-dropdown"
                                        >
                                            <option value="">-- None --</option>
                                            {Object.keys(users).sort().map(department => (
                                                <optgroup key={department} label={department}>
                                                    {users[department].map(user => (
                                                        <option key={user._id} value={user._id}>
                                                            {user.fullName}
                                                        </option>
                                                    ))}
                                                </optgroup>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label htmlFor="edit-additionalVerifier2">Additional Verifier 2</label>
                                        <select
                                            id="edit-additionalVerifier2"
                                            name="additionalVerifier2"
                                            value={editFormData.additionalVerifier2}
                                            onChange={handleEditInputChange}
                                            disabled={loadingUsers}
                                            className="user-dropdown"
                                        >
                                            <option value="">-- None --</option>
                                            {Object.keys(users).sort().map(department => (
                                                <optgroup key={department} label={department}>
                                                    {users[department].map(user => (
                                                        <option key={user._id} value={user._id}>
                                                            {user.fullName}
                                                        </option>
                                                    ))}
                                                </optgroup>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="info-note">
                                    <span className="info-icon">ℹ️</span>
                                    <p>Editing will not affect any signatures already collected.</p>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button 
                                    className="btn btn-secondary" 
                                    onClick={handleCloseEditModal}
                                    disabled={loading}
                                >
                                    Cancel
                                </button>
                                <button 
                                    className="btn btn-primary" 
                                    onClick={handleSubmitEdit}
                                    disabled={loading}
                                >
                                    {loading ? 'Updating...' : 'Update Entry'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {showReceiverModal && selectedEntry && (
                    <div className="modal-overlay" onClick={handleCloseReceiverModal}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2>Update Final Receiver</h2>
                                <button className="modal-close" onClick={handleCloseReceiverModal}>×</button>
                            </div>
                            <div className="modal-body">
                                {error && (
                                    <div className="alert alert-error">
                                        {error}
                                    </div>
                                )}

                                <div className="entry-details-readonly">
                                    <div className="detail-row">
                                        <label>From:</label>
                                        <span>{selectedEntry.from}</span>
                                    </div>
                                    <div className="detail-row">
                                        <label>To:</label>
                                        <span>{selectedEntry.to}</span>
                                    </div>
                                    <div className="detail-row">
                                        <label>Documents:</label>
                                        <span>{selectedEntry.documents}</span>
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="receiver">Final Receiver (Optional)</label>
                                    <small className="form-hint">Leave empty if document was received by the "To" person</small>
                                    <div className="input-with-toggle">
                                        <select
                                            name="receiverType"
                                            value={receiverFormData.receiverType}
                                            onChange={handleReceiverInputChange}
                                            className="type-selector"
                                        >
                                            <option value="user">Select User</option>
                                            <option value="other">Other (Custom)</option>
                                        </select>
                                        
                                        {receiverFormData.receiverType === 'user' ? (
                                            <select
                                                id="receiver"
                                                name="receiver"
                                                value={receiverFormData.receiver}
                                                onChange={handleReceiverInputChange}
                                                disabled={loadingUsers}
                                                className="user-dropdown"
                                            >
                                                <option value="">-- None --</option>
                                                {Object.keys(users).sort().map(department => (
                                                    <optgroup key={department} label={department}>
                                                        {users[department].map(user => (
                                                            <option key={user._id} value={user._id}>
                                                                {user.fullName}
                                                            </option>
                                                        ))}
                                                    </optgroup>
                                                ))}
                                            </select>
                                        ) : (
                                            <input
                                                type="text"
                                                id="receiver"
                                                name="receiver"
                                                value={receiverFormData.receiver}
                                                onChange={handleReceiverInputChange}
                                                placeholder="Enter custom name or leave empty"
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button 
                                    className="btn btn-secondary" 
                                    onClick={handleCloseReceiverModal}
                                    disabled={loading}
                                >
                                    Cancel
                                </button>
                                <button 
                                    className="btn btn-primary" 
                                    onClick={handleSubmitReceiver}
                                    disabled={loading}
                                >
                                    {loading ? 'Updating...' : 'Update Receiver'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DocumentLogBook;
